/**
 * screenshotHelper — capture a still frame from a <video> OR a <canvas> with a burned-in
 * cam/timestamp overlay.
 *
 * Extracted from the legacy PlaybackView.transportScreenshot so MCPlayback's per-cell snapshot
 * reuses the identical rendering + filename. The caller supplies the label text (legacy reads it
 * from its DOM; MCPlayback passes its slot's cam name + timestamp).
 *
 * The source is EITHER a <video> (local NVR / cloud-legacy, intrinsic size = videoWidth/videoHeight)
 * or a <canvas> (dragon WebCodecs playback, intrinsic size = width/height). Both are valid
 * drawImage() sources; we read whichever dimension pair is populated so the same capture path serves
 * every product type.
 */

// Intrinsic pixel dimensions of a drawable frame source (video OR canvas).
const sourceWidth = (el) => (el && (el.videoWidth || el.width)) || 0;
const sourceHeight = (el) => (el && (el.videoHeight || el.height)) || 0;

/** Overlay caption: "<cam>  <time>" when both present, else whichever exists. */
export const composeOverlayText = (camName = '', timestamp = '') =>
  camName && timestamp ? `${camName}  ${timestamp}` : camName || timestamp;

/** Download filename: spaces -> _, colons stripped, .png. Falls back to 'cam' / Date.now(). */
export const buildScreenshotFilename = (camName = '', timestamp = '') =>
  `screenshot_${camName.replace(/\s/g, '_') || 'cam'}_${timestamp.replace(/:/g, '') || Date.now()}.png`;

/**
 * Draw the current frame of `source` (a <video> or a <canvas>) to a canvas with the overlay
 * caption burned in.
 * @returns {{dataUrl: string, filename: string}|null}  null if the source has no frame yet.
 */
export const captureScreenshot = (source, { camName = '', timestamp = '' } = {}) => {
  const srcW = sourceWidth(source);
  const srcH = sourceHeight(source);
  if (!source || !srcW) return null;

  const canvas = document.createElement('canvas');
  canvas.width = srcW;
  canvas.height = srcH;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(source, 0, 0);

  const overlayText = composeOverlayText(camName, timestamp);
  if (overlayText) {
    const fontSize = Math.max(16, Math.round(srcH / 30));
    ctx.font = `bold ${fontSize}px monospace`;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    const textWidth = ctx.measureText(overlayText).width;
    ctx.fillRect(0, srcH - fontSize - 12, textWidth + 24, fontSize + 12);
    ctx.fillStyle = 'white';
    ctx.fillText(overlayText, 12, srcH - 8);
  }

  // toDataURL throws a SecurityError on a TAINTED canvas — an eview cross-NVR <video> is a different
  // origin with no crossOrigin attr, so drawing it taints the canvas. Return null (same as "no frame")
  // rather than throwing, so a single un-capturable cam can't unwind a bulk-snapshot loop. #86baz05kd
  try {
    return { dataUrl: canvas.toDataURL('image/png'), filename: buildScreenshotFilename(camName, timestamp) };
  } catch (e) {
    return null;
  }
};

/**
 * Why did a capture fail? "No frame yet" resolves itself once the picture is up; a TAINTED canvas never
 * will, because the frame came from a cross-origin eView member and the browser refuses the read-back.
 * Telling the user to wait for something that can never happen is the bug this distinguishes.
 * #86bb7phhh
 * @returns {'ok'|'no-frame'|'cross-origin'}
 */
export const captureFailureReason = (source) => {
  if (!source || !sourceWidth(source)) return 'no-frame';
  // The source HAS pixels, so a failed capture can only be the same-origin policy.
  return 'cross-origin';
};

/**
 * Capture + trigger a browser download of the screenshot. Returns true if a frame was available.
 */
export const downloadScreenshot = (video, labels) => {
  const shot = captureScreenshot(video, labels);
  if (!shot) return { ok: false, reason: captureFailureReason(video) };
  const link = document.createElement('a');
  link.download = shot.filename;
  link.href = shot.dataUrl;
  link.click();
  return { ok: true };
};

/**
 * Composite several frame sources into ONE grid image matching the on-screen multicam layout, with a
 * per-cell cam/timestamp overlay burned in. Cells are row-major; a null/frameless cell renders black so
 * the composite mirrors the grid (including empty slots). #86baz05kd
 *
 * Returns null if NO cell has a frame yet, OR if the composite canvas is tainted — a cross-origin
 * (eView cross-NVR) <video> with no crossOrigin attr taints the whole canvas, so toDataURL throws; the
 * caller distinguishes "no frames" from "tainted" by whether any source had a frame.
 *
 * @param {Array<{source, camName, timestamp}|null>} cells  one entry per grid cell, row-major
 * @param {{cols?: number, filename?: string}} opts
 * @returns {{dataUrl: string, filename: string}|null}
 */
export const captureCompositeScreenshot = (cells = [], { cols = 1, filename } = {}) => {
  const columns = Math.max(1, cols);
  const drawable = cells.filter((c) => c && c.source && sourceWidth(c.source));
  if (!drawable.length) return null;

  const rows = Math.ceil(cells.length / columns);
  // Uniform cell aspect from the first drawable source (cams in a grid are typically the same shape).
  const first = drawable[0].source;
  const aspect = (sourceHeight(first) / sourceWidth(first)) || 0.5625;
  const cellW = 640;
  const cellH = Math.max(1, Math.round(cellW * aspect));

  const canvas = document.createElement('canvas');
  canvas.width = columns * cellW;
  canvas.height = rows * cellH;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  cells.forEach((c, idx) => {
    const cx = (idx % columns) * cellW;
    const cy = Math.floor(idx / columns) * cellH;
    if (!(c && c.source && sourceWidth(c.source))) return;   // empty/frameless cell stays black
    const sw = sourceWidth(c.source), sh = sourceHeight(c.source);
    const scale = Math.min(cellW / sw, cellH / sh);           // contain (letterbox) to keep aspect
    const dw = sw * scale, dh = sh * scale;
    ctx.drawImage(c.source, cx + (cellW - dw) / 2, cy + (cellH - dh) / 2, dw, dh);
    const text = composeOverlayText(c.camName, c.timestamp);
    if (text) {
      const fs = Math.max(12, Math.round(cellH / 18));
      ctx.font = `bold ${fs}px monospace`;
      const tw = ctx.measureText(text).width;
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.fillRect(cx, cy + cellH - fs - 10, Math.min(tw + 16, cellW), fs + 10);
      ctx.fillStyle = 'white';
      ctx.fillText(text, cx + 8, cy + cellH - 6);
    }
  });

  try {
    return { dataUrl: canvas.toDataURL('image/png'), filename: filename || `mcplayback_grid_${Date.now()}.png` };
  } catch (e) {
    return null;   // tainted composite (cross-origin eView cam) — caller shows the "can't composite" toast
  }
};

/** Capture the on-screen grid as ONE composite image and trigger a download. Returns true on success. */
export const downloadComposite = (cells, opts) => {
  const shot = captureCompositeScreenshot(cells, opts);
  if (!shot) return false;
  const link = document.createElement('a');
  link.download = shot.filename;
  link.href = shot.dataUrl;
  link.click();
  return true;
};

// Draw one grid cell's frame (letterboxed) + its cam/timestamp overlay. Shared by the composite paths.
const drawGridCell = (ctx, source, { cx, cy, cellW, cellH, camName, timestamp }) => {
  const sw = (source && (source.width || source.videoWidth)) || 0;
  const sh = (source && (source.height || source.videoHeight)) || 0;
  if (source && sw) {
    const scale = Math.min(cellW / sw, cellH / sh);           // contain (letterbox), keep aspect
    const dw = sw * scale, dh = sh * scale;
    ctx.drawImage(source, cx + (cellW - dw) / 2, cy + (cellH - dh) / 2, dw, dh);
  }
  const text = composeOverlayText(camName, timestamp);
  if (text) {
    const fs = Math.max(12, Math.round(cellH / 18));
    ctx.font = `bold ${fs}px monospace`;
    const tw = ctx.measureText(text).width;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillRect(cx, cy + cellH - fs - 10, Math.min(tw + 16, cellW), fs + 10);
    ctx.fillStyle = 'white';
    ctx.fillText(text, cx + 8, cy + cellH - 6);
  }
};

/**
 * FETCH each cam's NVR JPEG and composite the bytes into ONE grid image (David 2026-08-16). Unlike
 * captureCompositeScreenshot (which draws the on-screen <video>/<canvas> and TAINTS on a cross-origin
 * eView <video>), this fetches the JPEG as a blob → createImageBitmap → drawImage, which does NOT taint —
 * both mpengine (`/mpe/cam.jpg`, live) and pbserver (playback frame-at-time) send `Access-Control-Allow-
 * Origin: *`, so cross-origin bytes are readable. Works for EVERY mode incl. eView. Per-cell failure
 * leaves that cell black and does NOT abort the batch. Same cell layout + overlay as the element path.
 *
 * @param {Array<{jpegUrl, camName, timestamp}|null>} cells  row-major; null = empty slot (black)
 * @param {{cols?: number, filename?: string, fetchImpl?: function, bitmapImpl?: function}} opts
 * @returns {Promise<{ok: boolean, captured: number, total: number}>}
 */
export const fetchCompositeSnapshot = async (cells = [], { cols = 1, filename, fetchImpl, bitmapImpl } = {}) => {
  const columns = Math.max(1, cols);
  const doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
  const toBitmap = bitmapImpl || (typeof createImageBitmap !== 'undefined' ? createImageBitmap : null);
  const total = cells.length;
  if (!doFetch || !toBitmap) return { ok: false, captured: 0, total };

  // Fetch every cell's JPEG concurrently; a failed/empty/non-image cell resolves to null (black cell).
  const frames = await Promise.all(cells.map(async (c) => {
    if (!c || !c.jpegUrl) return null;
    try {
      const res = await doFetch(c.jpegUrl, { credentials: 'omit' });
      if (!res || !res.ok) return null;
      const blob = await res.blob();
      if (!blob || !blob.size) return null;
      const bmp = await toBitmap(blob);
      return { bmp, camName: c.camName, timestamp: c.timestamp };
    } catch (e) {
      return null;
    }
  }));

  const drawable = frames.filter(Boolean);
  if (!drawable.length) return { ok: false, captured: 0, total };

  const first = drawable[0].bmp;
  const aspect = ((first.height || 0) / (first.width || 1)) || 0.5625;
  const cellW = 640;
  const cellH = Math.max(1, Math.round(cellW * aspect));
  const rows = Math.ceil(total / columns);

  const canvas = document.createElement('canvas');
  canvas.width = columns * cellW;
  canvas.height = rows * cellH;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  frames.forEach((f, idx) => {
    const cx = (idx % columns) * cellW;
    const cy = Math.floor(idx / columns) * cellH;
    drawGridCell(ctx, f && f.bmp, { cx, cy, cellW, cellH, camName: f && f.camName, timestamp: f && f.timestamp });
    if (f && f.bmp && f.bmp.close) { try { f.bmp.close(); } catch (e) { /* ignore */ } }
  });

  const link = document.createElement('a');
  link.download = filename || `dividia_grid_${Date.now()}.png`;
  link.href = canvas.toDataURL('image/png');   // fetched-blob bitmaps never taint → export always succeeds
  link.click();
  return { ok: true, captured: drawable.length, total };
};
